import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Building2, X, ChevronRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { smartSearch } from '../services/api';

const SmartSearchBar = ({ minimal = false }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ venues: [], cities: [], districts: [] });
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    // Click outside to close
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (value) => {
    setSearchQuery(value);

    if (value.length < 2) {
      setSearchResults({ venues: [], cities: [], districts: [] });
      setShowDropdown(false);
      return;
    }

    // Debounce search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await smartSearch(value);
        setSearchResults(response.data);
        setShowDropdown(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleVenueClick = (venue) => {
    navigate(`/${venue.slug}`);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleCityClick = (city) => {
    // Use ID for more reliable filtering
    if (city.id) {
      navigate(`/davet-evleri?cityId=${city.id}`);
    } else {
      navigate(`/davet-evleri?citySlug=${city.slug}`);
    }
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleDistrictClick = (district) => {
    // Use IDs for more reliable filtering
    if (district.id && district.cityId) {
      navigate(`/davet-evleri?cityId=${district.cityId}&districtIds=${district.id}`);
    } else {
      navigate(`/davet-evleri?districtSlug=${district.slug}`);
    }
    setShowDropdown(false);
    setSearchQuery('');
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults({ venues: [], cities: [], districts: [] });
    setShowDropdown(false);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/davet-evleri?q=${encodeURIComponent(searchQuery)}`);
      setShowDropdown(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const hasResults = searchResults.venues.length > 0 || 
                     searchResults.cities.length > 0 || 
                     searchResults.districts.length > 0;

  return (
    <div className="w-full max-w-5xl mx-auto relative z-[9999]" ref={searchRef}>
      {/* Minimal design for hero section */}
      {minimal ? (
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-500 z-10" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                if (searchQuery.length >= 2 && hasResults) {
                  setShowDropdown(true);
                }
              }}
              placeholder="Mekan, şehir veya ilçe ara..."
              className="h-14 pl-12 pr-12 text-base border-0 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg focus:ring-2 focus:ring-pink-500/30 font-medium placeholder:text-gray-400"
            />
            {searchQuery ? (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-pink-500 hover:bg-pink-600 rounded-xl flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            )}
          </div>

          {/* Dropdown Results */}
          {showDropdown && (
            <div className="absolute z-[99999] w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-[60vh] overflow-y-auto">
              {loading ? (
                <div className="flex justify-center items-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-600"></div>
                </div>
              ) : hasResults ? (
                <div>
                  {/* Cities */}
                  {searchResults.cities.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-3 py-1.5 bg-pink-50 text-xs font-semibold text-pink-700 uppercase">
                        Şehirler
                      </div>
                      {searchResults.cities.map((city) => (
                        <button
                          key={city.name}
                          onClick={() => handleCityClick(city)}
                          className="w-full text-left px-3 py-2.5 hover:bg-pink-50 transition-colors flex items-center gap-2"
                        >
                          <MapPin className="w-4 h-4 text-pink-600" />
                          <span className="font-medium text-gray-900">{city.name}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Districts */}
                  {searchResults.districts.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-3 py-1.5 bg-purple-50 text-xs font-semibold text-purple-700 uppercase">
                        İlçeler
                      </div>
                      {searchResults.districts.map((district) => (
                        <button
                          key={district.name + district.cityName}
                          onClick={() => handleDistrictClick(district)}
                          className="w-full text-left px-3 py-2.5 hover:bg-purple-50 transition-colors flex items-center gap-2"
                        >
                          <Building2 className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-gray-900">{district.name}</span>
                          <span className="text-xs text-gray-500">({district.cityName})</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Venues */}
                  {searchResults.venues.length > 0 && (
                    <div>
                      <div className="px-3 py-1.5 bg-amber-50 text-xs font-semibold text-amber-700 uppercase">
                        Mekanlar
                      </div>
                      {searchResults.venues.map((venue) => (
                        <button
                          key={venue.id}
                          onClick={() => handleVenueClick(venue)}
                          className="w-full text-left px-3 py-2.5 hover:bg-amber-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                              {venue.name?.charAt(0)}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{venue.name}</div>
                              <div className="text-xs text-gray-500">{venue.districtName}, {venue.cityName}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-6 text-center text-gray-500 text-sm">
                  Sonuç bulunamadı
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Original full design */
        <div className="px-4 sm:px-0">
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-2 sm:p-3 shadow-2xl border border-white/30">
            <div className="bg-white rounded-2xl shadow-lg p-3 sm:p-4">
              <div className="flex gap-3">
                {/* Smart Search Input - Full Width */}
                <div className="w-full relative">
                  <div className="relative">
                    <Search className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 sm:w-5 sm:h-5 text-pink-600 z-10" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onFocus={() => {
                        if (searchQuery.length >= 2 && hasResults) {
                          setShowDropdown(true);
                        }
                      }}
                      placeholder="Mekan, şehir veya ilçe ara..."
                      className="h-12 sm:h-14 lg:h-16 pl-11 sm:pl-12 lg:pl-14 pr-10 text-sm sm:text-base lg:text-lg border-2 border-gray-200 focus:border-pink-500 rounded-xl transition-all font-medium"
                    />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Dropdown Results */}
                  {showDropdown && (
                    <div className="absolute z-[100] w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-pink-300 scrollbar-track-gray-100">
                  {loading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
                    </div>
                  ) : hasResults ? (
                    <div>
                      {/* Cities - Priority 1 */}
                      {searchResults.cities.length > 0 && (
                        <div className="border-b border-gray-100">
                          <div className="px-4 py-2 bg-gradient-to-r from-pink-50 to-purple-50 text-xs font-semibold text-pink-700 uppercase">
                            Şehirler
                          </div>
                          {searchResults.cities.map((city) => (
                            <button
                              key={city.name}
                              onClick={() => handleCityClick(city)}
                              className="w-full text-left px-4 py-3 hover:bg-pink-50 transition-colors border-b border-gray-50 last:border-b-0"
                            >
                              <div className="flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-pink-600" />
                                <span className="font-semibold text-gray-900">{city.name}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Districts - Priority 2 */}
                      {searchResults.districts.length > 0 && (
                        <div className="border-b border-gray-100">
                          <div className="px-4 py-2 bg-gradient-to-r from-purple-50 to-indigo-50 text-xs font-semibold text-purple-700 uppercase">
                            İlçeler
                          </div>
                          {searchResults.districts.map((district) => (
                            <button
                              key={district.name + district.cityName}
                              onClick={() => handleDistrictClick(district)}
                              className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b border-gray-50 last:border-b-0"
                            >
                              <div className="flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-purple-600" />
                                <span className="font-semibold text-gray-900">{district.name}</span>
                                <span className="text-sm text-gray-500">({district.cityName})</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Venues - Priority 3 (Premium or Most Reviewed) */}
                      {searchResults.venues.length > 0 && (
                        <div>
                          <div className="px-4 py-2 bg-gradient-to-r from-amber-50 to-orange-50 text-xs font-semibold text-amber-700 uppercase flex items-center gap-2">
                            <span>Önerilen Davet Evleri</span>
                          </div>
                          {searchResults.venues.map((venue) => (
                            <button
                              key={venue.id}
                              onClick={() => handleVenueClick(venue)}
                              className="w-full text-left px-3 py-2.5 hover:bg-amber-50 transition-colors border-b border-gray-50 last:border-b-0"
                            >
                              <div className="flex items-center gap-2.5">
                                {venue.images && venue.images.length > 0 && (
                                  <img 
                                    src={venue.images[0]} 
                                    alt={venue.name}
                                    className="w-12 h-12 object-cover rounded-lg border-2 border-amber-200 flex-shrink-0"
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-gray-900 text-sm flex items-center gap-1.5 truncate">
                                    <span className="truncate">{venue.name}</span>
                                    {venue.isPremium && (
                                      <span className="text-[10px] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-1.5 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">
                                        Premium
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{venue.districtName}, {venue.cityName}</span>
                                  </div>
                                  {venue.reviewCount > 0 && (
                                    <div className="text-[11px] text-gray-400 mt-0.5">
                                      ⭐ {venue.reviewCount} yorum
                                    </div>
                                  )}
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : searchQuery.length >= 2 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <p className="font-medium">Sonuç bulunamadı</p>
                      <p className="text-sm mt-1">Farklı bir arama terimi deneyin</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
      )}
    </div>
  );
};

export default SmartSearchBar;
